import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import {
  AuthLayout,
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  AuthActionPage,
  MfaChallengePage,
  MfaSetupPage,
  AcceptInvitePage,
  PrivateRoute,
} from '@/features/auth';
import { DashboardPage } from '@/features/dashboard';
import { ChoreWheelPage } from '@/features/chore-wheel';
import { TasksPage } from '@/features/tasks';
import { ExpensesPage } from '@/features/expenses';
import { NotesPage } from '@/features/notes';
import { DatesPage } from '@/features/dates';
import { SettingsPage } from '@/features/settings';
import { ItemDetailPage } from '@/features/items';
import { NotificationsPage } from '@/features/notifications';
import { SearchResultsPage } from '@/features/search';
import { useAuthStore, useUIStore } from '@/stores';
import { setOnAuthFailureCallback } from '@perfin/shared';

export default function App() {
  const initialize = useAuthStore((state) => state.initialize);

  // Initialize auth listener at app level
  useEffect(() => {
    const unsubscribe = initialize();
    return () => unsubscribe();
  }, [initialize]);

  // Set up auth failure callback for session expiration handling
  useEffect(() => {
    setOnAuthFailureCallback(() => {
      useUIStore.getState().addToast({
        type: 'warning',
        message: 'Session expired. Please log in again.',
        duration: 4000,
      });
      // Redirect to login
      window.location.href = '/login';
    });
  }, []);

  return (
    <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />
          <Route path="/mfa-challenge" element={<MfaChallengePage />} />
          <Route path="/mfa-setup" element={<MfaSetupPage />} />
        </Route>

        {/* Invitation acceptance (can be accessed with or without auth) */}
        <Route path="/invite/:token" element={<AcceptInvitePage />} />

        {/* Protected routes */}
        <Route
          element={
            <PrivateRoute>
              <AppLayout />
            </PrivateRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/chore-wheel" element={<ChoreWheelPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/:id" element={<ItemDetailPage />} />
          <Route path="/transactions" element={<ExpensesPage />} />
          <Route path="/transactions/:id" element={<ItemDetailPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/notes/:id" element={<ItemDetailPage />} />
          <Route path="/dates" element={<DatesPage />} />
          <Route path="/dates/:id" element={<ItemDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Unified item routes */}
          <Route path="/items/new" element={<ItemDetailPage />} />
          <Route path="/items/:id" element={<ItemDetailPage />} />
        </Route>

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}
